import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LanguageIcon from '@mui/icons-material/Language';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import moment from 'moment';
import { api } from '../../helpers/apiUtils';
import { NurAdmin, useIstAdmin } from '../../components/marketingForm';
import { TECHNIK_KONTAKT } from './texteLive';

// Deutsche Zahlen überall (Persona-Runde 1: „26.9“, „246.3“, „1.6 %“ lasen sich wie Tausender).
function zahl(v: number | null | undefined, stellen = 0): string {
  if (v == null || !Number.isFinite(v)) return '–';
  return v.toLocaleString('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  });
}

/**
 * Begriffe der Statistik (Persona-Runde 1, Felix: „Peak“, „Watch-Through“, „Retention“, „Embed“,
 * „Iframe“, „Unique“). Es gibt ZWEI Höchststände, die verschieden gemessen werden — beide Namen
 * sagen deshalb, WO gezählt wurde:
 * - alle Player: live_sessions.peak_viewer_count, geschrieben von LiveViewerCounter aus den
 *   Heartbeats ALLER Player (live.finanztrends.de und eingebettete Player), höchster Stand
 *   gleichzeitig offener Player während der Live-Phase.
 * - nur live.finanztrends.de: engagement.standalone_peak_simultaneous, aus den Heartbeat-Intervallen
 *   der Besucher mit is_embed = 0 rekonstruiert.
 */
const HOECHST_ALLE = 'Höchststand alle Player';
const HOECHST_ALLE_TIPP =
  'Die meisten Zuschauer, die in einem Moment der Live-Sendung gleichzeitig zugesehen haben — wie eine Momentaufnahme, auf live.finanztrends.de und in den eingebetteten Playern auf Finanztrends- und Partnerseiten zusammen. Nicht alle, die im Lauf der Sendung irgendwann dabei waren: wer früh geht und wer spät kommt, ist nie gleichzeitig im Bild.';
const HOECHST_SEITE = 'Höchststand nur live.finanztrends.de';
const HOECHST_SEITE_TIPP =
  'Höchste Zahl gleichzeitig anwesender Zuschauer nur auf live.finanztrends.de selbst — ohne die eingebetteten Player auf Artikelseiten. Deshalb meist kleiner als der Höchststand aller Player. Ermittelt aus den Meldungen, die der Player alle 15 Sekunden schickt.';
const EINGEBETTET = 'im eingebetteten Player (Finanztrends- und Partnerseiten)';

interface AutoRollInfo {
  video_id: number;
  title: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  video_duration_seconds: number | null;
  peak_viewer_count: number;
  completion_pct: number | null;
}

interface SessionEngagement {
  iframe_inline_total: number;
  iframe_engaged_30s: number;
  iframe_engaged_60s: number;
  iframe_engaged_5min: number;
  click_through_total: number;
  click_through_engaged_30s: number;
  direct_total: number;
  // Standalone-page cohort (marketing definition of "live.finanztrends.de
  // viewers"): click-through + direct combined, iframe-inline excluded.
  standalone_unique_total: number;
  // Peak simultaneous on the standalone page — reconstructed from heartbeat
  // intervals in live_viewer_attribution (is_embed=0). Only meaningful for
  // sessions with has_engagement_data=true.
  standalone_peak_simultaneous: number;
  iframe_avg_watch_seconds: number | null;
  // Ø Watch-Time of the standalone-page cohort (is_embed=0). null when the
  // session had no standalone viewers with heartbeat data.
  standalone_avg_watch_seconds: number | null;
  has_engagement_data: boolean;
  // Geräte-Split aus viewport_class (window.innerWidth >= 600 im Player).
  // Im Embed ist das die Iframe-Breite, nicht das Gerät — brauchbarer Proxy,
  // keine UA-Erkennung. has_viewport_data trennt "nicht erhoben" (Sendungen
  // vor 2026-06-01) von einem echten Null-Wert.
  iframe_mobile: number;
  iframe_desktop: number;
  standalone_mobile: number;
  standalone_desktop: number;
  iframe_mobile_avg_watch_seconds: number | null;
  iframe_desktop_avg_watch_seconds: number | null;
  has_viewport_data: boolean;
}

// Zuschauer der Auto-Roll-Phase. `exact` unterscheidet die beiden Quellen:
// true  = aus den Phasen-Spalten (ab Migration 2026-08-14), inkl. Stufen
// false = aus dem Heartbeat-Fenster rekonstruiert, Stufen nicht ermittelbar
// exact=false → aus dem Heartbeat-Verlauf geschaetzt (Sendung vor der
// Phasen-Messung, erste exakte Sendung 17.08.2026).
// available=false → die Sendung liegt vor der Heartbeat-Erfassung (20.06.2026),
// die Schaetzung kann nichts sagen. Im UI ein Strich, keine 0.
interface AutoRollEngagement {
  exact: boolean;
  available: boolean;
  total: number;
  embed: number;
  standalone: number;
  engaged_30s: number | null;
  engaged_60s: number | null;
  engaged_5min: number | null;
  avg_watch_seconds: number | null;
}

// available=false → DB14 war nicht erreichbar; im UI ein Strich, keine 0.
// exact=false → über die geklickten CTA-URLs am Sendetag geschätzt.
interface SessionAnfos {
  available: boolean;
  exact: boolean;
  count: number;
}

interface SessionStat {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  peak_viewer_count: number;
  cta_clicks: number;
  cta_clicks_live: number;
  cta_clicks_auto_roll: number;
  cta_unique: number;
  click_rate: number;
  auto_roll: AutoRollInfo | null;
  engagement?: SessionEngagement;
  show_name?: string | null;
  auto_roll_engagement?: AutoRollEngagement | null;
  anfos?: SessionAnfos;
}

interface StatsSummary {
  session_count: number;
  total_hours: number;
  avg_peak_viewer: number;
  total_cta_clicks: number;
  total_cta_clicks_live: number;
  total_cta_clicks_auto_roll: number;
  avg_click_rate: number;
  active_viewers_now: number;
  auto_roll_session_count: number;
  auto_roll_avg_peak_viewer: number;
  auto_roll_avg_completion_pct: number | null;
  auto_roll_avg_retention_ratio: number | null;
  // live.finanztrends.de standalone (marketing "who visited the actual page"):
  // click-through + direct only, iframe-inline excluded.
  total_standalone_unique: number;
  avg_standalone_peak_simultaneous: number;
  standalone_data_session_count: number;
}

interface SnapshotPoint {
  captured_at: string;
  elapsed_sec: number | null;
  phase: 'live' | 'auto-roll';
  active_count: number;
}

interface EmbedAbVariant {
  variant: string;
  impressions: number;
  clicks: number;
  unique_visitors: number;
  unique_clickers: number;
  unique_visitors_thresholded?: boolean;
  unique_clickers_thresholded?: boolean;
  ctr: number;
}

interface EmbedAbSurfaceVariant extends EmbedAbVariant {
  surface: string;
  impression_share_pct?: number;
  click_share_pct?: number;
}

interface EmbedAbData {
  total_impressions: number;
  total_clicks: number;
  by_variant: EmbedAbVariant[];
  by_surface_variant: EmbedAbSurfaceVariant[];
  balance_warning: boolean;
  no_clicks_warning: boolean;
  small_sample_warning: boolean;
  source?: 'ga4' | 'unavailable';
  surface_data_quality?: 'trusted' | 'inflated' | 'unknown';
  surface_inflation_factor?: number;
  click_data_source?: 'ga4' | 'attribution_landings' | 'mixed';
  click_data_trusted_since?: string;
  error?: string;
}

// Helper: render a unique-user count, showing "<5" when GA4 privacy-thresholded
// the value to 0 (i.e. eventCount > 0 but totalUsers = 0).
const renderUnique = (count: number, thresholded?: boolean): string => {
  if (thresholded) return '<5';
  if (count === 0) return '–';
  return zahl(count);
};

// Publisher attribution for multi-publisher embed deployments. surface values
// like `bg_sidebar` carry the publisher prefix; `sidebar` / `hero_desktop` /
// `hero_mobile` (bare) are Finanztrends-native. Keep this list in sync with
// `embed.js` getPublisher() sanitization (lowercase ASCII+digits, max 12 chars)
// and the BG handover at /tmp/multi-publisher-live-embed-handover.md.
const KNOWN_PUBLISHERS: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'ft', label: 'Finanztrends' },
  { slug: 'bg', label: 'Boerse-Global' },
  { slug: 'sw', label: 'Stock-World' },
  { slug: 'be', label: 'Boerse-Express' },
  { slug: 'tt', label: 'Trading-Treff' },
  { slug: 'kme', label: 'Kapitalmarktexperten' },
];
const PUBLISHER_PREFIX_SET = new Set(
  KNOWN_PUBLISHERS.filter((p) => p.slug !== 'ft').map((p) => p.slug),
);
const detectPublisher = (surface: string): string => {
  const m = surface.match(/^([a-z0-9]+)_/);
  if (m && PUBLISHER_PREFIX_SET.has(m[1])) return m[1];
  return 'ft';
};
const publisherLabel = (slug: string): string =>
  KNOWN_PUBLISHERS.find((p) => p.slug === slug)?.label ?? slug;

// Publisher role + tone for cards. `role` describes the publisher's relationship
// to FT — own site vs partner — so marketing can read it without context.
type PublisherRole = 'own' | 'partner';
const publisherMeta: Record<string, { role: PublisherRole; description: string }> = {
  ft: { role: 'own', description: 'Eigene Website (finanztrends.de)' },
  bg: { role: 'partner', description: 'Schwester-Website Boerse-Global' },
  sw: { role: 'partner', description: 'Schwester-Website Stock-World' },
  be: { role: 'partner', description: 'Schwester-Website Boerse-Express' },
  tt: { role: 'partner', description: 'Schwester-Website Trading-Treff' },
  kme: { role: 'partner', description: 'Schwester-Website Kapitalmarktexperten' },
};

// Translate technical surface names into marketing-readable labels.
// Examples:
//   sidebar         → "Sidebar (Artikelseite)"
//   hero_desktop    → "Hauptbanner Startseite (Desktop)"
//   hero_mobile     → "Hauptbanner Startseite (Mobil)"
//   bg_sidebar      → "Sidebar (Artikelseite)" — publisher rendered separately
const surfaceDisplayLabel = (surface: string): string => {
  const pub = detectPublisher(surface);
  const bare = pub === 'ft' ? surface : surface.slice(pub.length + 1);
  switch (bare) {
    case 'sidebar':
      return 'Seitenleiste (Artikelseite)';
    case 'hero_desktop':
      return 'Hauptbanner Startseite (Desktop)';
    case 'hero_mobile':
      return 'Hauptbanner Startseite (Handy)';
    default:
      return bare;
  }
};

// Group surfaces into "placements" for the bar chart (publisher-agnostic).
const PLACEMENT_ORDER = ['hero_desktop', 'hero_mobile', 'sidebar'];
const placementBareKey = (surface: string): string => {
  const pub = detectPublisher(surface);
  return pub === 'ft' ? surface : surface.slice(pub.length + 1);
};
const placementShortLabel = (key: string): string => {
  switch (key) {
    case 'sidebar':
      return 'Seitenleiste (Artikel)';
    case 'hero_desktop':
      return 'Startseite (Desktop)';
    case 'hero_mobile':
      return 'Startseite (Handy)';
    default:
      return key;
  }
};

// Variant marketing labels — "poster" is the static Live-Bild, "iframe" is the
// real HLS player. Marketing should never see the raw token.
const variantDisplayLabel: Record<string, string> = {
  poster: 'Live-Bild',
  iframe: 'Live-Player',
};
const variantShortLabel: Record<string, string> = {
  poster: 'Live-Bild',
  iframe: 'Live-Player',
};
const variantTooltip: Record<string, string> = {
  poster:
    'Ein festes Vorschaubild mit LIVE-Hinweis und Überschrift. Ein Klick öffnet die Sendung auf live.finanztrends.de. Lädt schneller und belastet die Seite weniger.',
  iframe:
    'Der echte Video-Player direkt auf der Seite. Wer auf Abspielen klickt, sieht die Sendung dort — ein Klick darauf öffnet zusätzlich live.finanztrends.de.',
};

// CTR colour-coding for at-a-glance reads.
const ctrChipColor = (ctr: number): 'default' | 'success' | 'warning' | 'error' => {
  if (ctr >= 0.1) return 'success';
  if (ctr >= 0.03) return 'warning';
  if (ctr > 0) return 'default';
  return 'error';
};
const formatCtr = (ctr: number): string => `${zahl(ctr * 100, 1)} %`;

// Click-metric trust gate. Flipped back to `true` on 2026-06-20 after embed.js
// `attachEmbedClickHandlers` switched the first listener from `pointerdown` →
// `pointerup` — pointerup only fires on completed taps, scroll gestures emit
// `pointercancel` and never trigger pointerup. The flag is kept rather than
// removed because (a) the prior corruption may persist in GA4 data for the
// window before the embed.js pointerup deploy reaches enough users via the
// 30-second cache, and (b) the dashboard wants a single switch if any future
// click-pipeline regression is suspected. Setting to false hides Klicker / CTR
// / winner badge / CTR-based bar chart and shows a fallback Seher-only view.
const CLICK_METRICS_TRUSTED = true;

interface StatsResponse {
  success: boolean;
  from: string;
  to: string;
  summary: StatsSummary;
  sessions: SessionStat[];
  embed_ab?: EmbedAbData;
}

function formatDuration(secs: number | null): string {
  if (secs == null || secs <= 0) return '–';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h > 0) return `${h} Std. ${m} Min.`;
  return `${m} Min.`;
}

function formatPercent(rate: number): string {
  if (!isFinite(rate) || rate <= 0) return '–';
  return `${zahl(rate * 100, 1)} %`;
}

// m:ss — die Watch-Time-Darstellung der Engagement-Tabelle. Bewusst getrennt
// von formatDuration (h/m), das für Sendungslängen gedacht ist.
function formatWatch(secs: number | null): string {
  if (secs == null) return '–';
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

const KpiCard: FC<{
  label: string;
  value: string | number;
  sublabel?: string;
  tooltip?: string;
}> = ({ label, value, sublabel, tooltip }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      // Umbrechen statt die Seite zu verbreitern: mindestens 160 px je Kachel, Rest teilt sich auf.
      flex: '1 1 160px',
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      minWidth: 0,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip}>
          <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help', fontSize: 14 }} />
        </Tooltip>
      )}
    </Stack>
    <Typography variant="h5" fontWeight={600} sx={{ mt: 0.5, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {sublabel && (
      <Typography variant="caption" color="textSecondary">
        {sublabel}
      </Typography>
    )}
  </Paper>
);

const Stats: FC = () => {
  const [from, setFrom] = useState(() => moment().subtract(29, 'days').format('YYYY-MM-DD'));
  const [to, setTo] = useState(() => moment().format('YYYY-MM-DD'));
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [snapshotSessionId, setSnapshotSessionId] = useState<number | null>(null);
  const [snapshotData, setSnapshotData] = useState<{
    snapshots: SnapshotPoint[];
    phase_switch_sec: number | null;
  } | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [publisherFilter, setPublisherFilter] = useState<string>('all');
  // Rohe Platz-Kennungen (bg_sidebar …) nur für den Admin (Anzeige-Weiche, kein Rechte-Riegel).
  const istAdmin = useIstAdmin();

  // Reset stale publisher filter: when the date range changes and the previously-
  // selected publisher (e.g. 'bg') has zero rows in the new response, the dropdown
  // hides itself (single-publisher UX) but the filter would otherwise keep
  // suppressing all rows → empty table dead-end. Force back to 'all' so the
  // operator always sees the available rows.
  useEffect(() => {
    if (publisherFilter === 'all') return;
    const available = new Set(
      (data?.embed_ab?.by_surface_variant ?? []).map((r) => detectPublisher(r.surface)),
    );
    if (!available.has(publisherFilter)) setPublisherFilter('all');
  }, [data?.embed_ab?.by_surface_variant, publisherFilter]);

  useEffect(() => {
    if (snapshotSessionId == null) {
      setSnapshotData(null);
      return;
    }
    let cancelled = false;
    setSnapshotLoading(true);
    api
      .get<{
        success: boolean;
        snapshots: SnapshotPoint[];
        phase_switch_sec: number | null;
      }>('/api/live/admin/stats/snapshots', { params: { session_id: snapshotSessionId } })
      .then((res) => {
        if (cancelled) return;
        setSnapshotData({
          snapshots: res.data.snapshots || [],
          phase_switch_sec: res.data.phase_switch_sec,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSnapshotData({ snapshots: [], phase_switch_sec: null });
      })
      .finally(() => {
        if (!cancelled) setSnapshotLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotSessionId]);

  const dropOffLine = useMemo(() => {
    if (!snapshotData) return [];
    const live = snapshotData.snapshots
      .filter((p) => p.phase === 'live' && p.elapsed_sec != null)
      .map((p) => ({ x: p.elapsed_sec as number, y: p.active_count }));
    const ar = snapshotData.snapshots
      .filter((p) => p.phase === 'auto-roll' && p.elapsed_sec != null)
      .map((p) => ({ x: p.elapsed_sec as number, y: p.active_count }));
    const out: { id: string; data: { x: number; y: number }[] }[] = [];
    if (live.length > 0) out.push({ id: 'Live', data: live });
    if (ar.length > 0) out.push({ id: 'Werbevideo', data: ar });
    return out;
  }, [snapshotData]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<StatsResponse>('/api/live/admin/stats', { params: { from, to } });
      setData(res.data);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Statistik konnte nicht geladen werden',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const sessionsAsc = useMemo(
    () => [...(data?.sessions || [])].sort((a, b) => a.started_at.localeCompare(b.started_at)),
    [data],
  );

  const peakLine = useMemo(
    () => [
      {
        id: 'peak_viewer',
        data: sessionsAsc.map((s) => ({
          x: moment.utc(s.started_at).format('DD.MM.'),
          y: s.peak_viewer_count,
        })),
      },
    ],
    [sessionsAsc],
  );

  const clicksBar = useMemo(
    () =>
      sessionsAsc.map((s) => ({
        date: moment.utc(s.started_at).format('DD.MM.'),
        Klicks: s.cta_clicks,
      })),
    [sessionsAsc],
  );

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Kennzahlen der Sendungen im gewählten Zeitraum. „Aktiv jetzt“ zählt, wer den Player gerade
        offen hat. „Höchststand“ heißt: die meisten Zuschauer gleichzeitig in einem Moment der
        Sendung. „Klicks“ heißt immer: Klicks auf Einblendungen während der Sendung und auf den
        Knopf im Werbevideo danach. Das Fragezeichen an einer Kachel erklärt sie genauer.
      </Typography>

      <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          label="Von"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Bis"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        {loading && <CircularProgress size={20} />}
      </Stack>

      {data && (
        <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
          <KpiCard label="Sendungen" value={zahl(data.summary.session_count)} />
          <KpiCard
            label="Sendestunden"
            value={zahl(data.summary.total_hours, 1)}
            sublabel="Summe Sendezeit"
          />
          <KpiCard
            label={`Ø ${HOECHST_ALLE}`}
            value={zahl(data.summary.avg_peak_viewer, 1)}
            sublabel="gleichzeitig, je Sendung"
            tooltip={`${HOECHST_ALLE_TIPP} Hier gemittelt über die Sendungen im Zeitraum.`}
          />
          <KpiCard
            label="Klicks gesamt"
            value={zahl(data.summary.total_cta_clicks)}
            sublabel="Einblendungen + Werbevideo"
          />
          <KpiCard
            // Renamed 2026-07-02 from "Ø Klickrate" (falsely read as Embed-CTR).
            // Backend: Summe Klicks ÷ Summe peak_viewer_count über die Sendungen
            // (routes/live.ts summary.avg_click_rate) — eine Intensität je Zuschauer im
            // Höchststand, NICHT die Klickrate der eingebetteten Player weiter unten.
            label="Klicks je Zuschauer"
            value={formatPercent(data.summary.avg_click_rate)}
            sublabel={`Klicks ÷ ${HOECHST_ALLE}`}
            tooltip="Alle Klicks im Zeitraum geteilt durch die Summe der Höchststände aller Player. Ein grober Anhaltspunkt, wie viele der gleichzeitig Anwesenden klicken — keine Klickrate über alle Personen."
          />
          <KpiCard
            label="Aktiv jetzt"
            value={zahl(data.summary.active_viewers_now)}
            sublabel="Player gerade offen"
          />
        </Stack>
      )}

      {data && data.summary.standalone_data_session_count > 0 && (
        <>
          <Typography variant="overline" color="textSecondary" fontWeight={600} sx={{ mt: 1 }}>
            Auf live.finanztrends.de selbst
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" mb={1}>
            Zuschauer, die die Seite live.finanztrends.de geöffnet haben — per Klick auf den Player
            auf einer Finanztrends- oder Partnerseite oder direkt. Wer nur {EINGEBETTET} schaut,
            zählt hier nicht. Grundlage: {zahl(data.summary.standalone_data_session_count)} von{' '}
            {zahl(data.summary.session_count)} Sendungen mit Zuschauer-Messung (seit 20.06.2026).
          </Typography>
          <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
            <KpiCard
              label="Verschiedene Zuschauer"
              value={zahl(data.summary.total_standalone_unique)}
              sublabel="je Sendung gezählt, summiert"
              tooltip={`Verschiedene Personen, die live.finanztrends.de geöffnet haben. Jede Person zählt einmal je Sendung; wer an zwei Tagen schaut, zählt zweimal. Wer nur ${EINGEBETTET} schaut, ist nicht enthalten.`}
            />
            <KpiCard
              label={`Ø ${HOECHST_SEITE}`}
              value={zahl(data.summary.avg_standalone_peak_simultaneous, 1)}
              sublabel="gleichzeitig, je Sendung"
              tooltip={`${HOECHST_SEITE_TIPP} Hier gemittelt über die Sendungen.`}
            />
          </Stack>
        </>
      )}

      {data && data.summary.auto_roll_session_count > 0 && (
        <>
          <Typography variant="overline" color="textSecondary" fontWeight={600} sx={{ mt: 1 }}>
            Werbevideo nach der Sendung
          </Typography>
          <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
            <KpiCard
              label="Sendungen mit Werbevideo"
              value={zahl(data.summary.auto_roll_session_count)}
              sublabel={`von ${zahl(data.summary.session_count)} (${zahl((data.summary.auto_roll_session_count / Math.max(1, data.summary.session_count)) * 100)} %)`}
            />
            <KpiCard
              label="Ø Höchststand im Werbevideo"
              value={zahl(data.summary.auto_roll_avg_peak_viewer, 1)}
              sublabel="gleichzeitig, je Sendung"
              tooltip="Die meisten Zuschauer, die in einem Moment gleichzeitig das Werbevideo nach der Sendung gesehen haben — gemittelt über die Sendungen mit Werbevideo."
            />
            <KpiCard
              label="Ø Werbevideo abgespielt"
              value={
                data.summary.auto_roll_avg_completion_pct != null
                  ? `${zahl(data.summary.auto_roll_avg_completion_pct)} %`
                  : '–'
              }
              sublabel="Anteil der Videolänge"
              tooltip="Wie viel vom Werbevideo lief, bevor es endete oder beendet wurde — gemessen an der Länge des Videos. Sagt nichts darüber, wie lange einzelne Zuschauer zugesehen haben."
            />
            <KpiCard
              label="Ø blieben fürs Werbevideo"
              value={formatPercent(data.summary.auto_roll_avg_retention_ratio || 0)}
              sublabel="Höchststand Werbevideo ÷ Sendung"
              tooltip="Wie viele Zuschauer nach der Sendung noch fürs Werbevideo dageblieben sind. 10 % hieße: Von 100 Zuschauern, die zur Sendung gleichzeitig da waren, schauten etwa 10 auch das Werbevideo. Gerechnet aus den beiden Höchstständen (Werbevideo geteilt durch Sendung), gemittelt über die Sendungen."
            />
            <KpiCard
              label="Klicks Sendung / Werbevideo"
              value={`${zahl(data.summary.total_cta_clicks_live)} / ${zahl(data.summary.total_cta_clicks_auto_roll)}`}
              sublabel="Klicks je Teil"
              tooltip="Links: Klicks während der Live-Sendung (Einblendungen). Rechts: Klicks auf den Knopf im Werbevideo nach der Sendung. Vor den Einblendungen gab es während der Sendung meist keinen Knopf — bei älteren Sendungen steht links deshalb oft 0."
            />
          </Stack>
        </>
      )}

      {data?.embed_ab && data.embed_ab.source === 'unavailable' && (
        <>
          <Typography variant="overline" color="textSecondary" fontWeight={600} sx={{ mt: 1 }}>
            Player auf Finanztrends- und Partnerseiten — Google Analytics
          </Typography>
          {/* Gelb, weil nur dieser Abschnitt fehlt: GA4 speist allein embed_ab
              (routes/live.ts, getLiveEmbedAbStatsCorrected im try/catch). Kacheln, „Wer schaut wie
              lange?“, Grafiken und Sendungstabelle kommen aus live_sessions, live_cta_clicks,
              live_viewer_attribution (lokale DB) und den Bestellungen (DB14). */}
          <Alert severity="warning" sx={{ fontSize: 13, mb: 1.5, mt: 0.5 }}>
            <strong>Zahlen aus Google Analytics fehlen gerade — nur dieser Abschnitt.</strong> Es
            fehlt, wie oft der Player auf Finanztrends- und Partnerseiten gezeigt wurde, wie viele
            Personen ihn gesehen und angeklickt haben, je Website, Platz und Fassung.
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              Alle übrigen Zahlen auf dieser Seite sind vollständig und stimmen: die Kacheln oben,
              „Wer schaut wie lange?“, die Grafiken und die Tabelle je Sendung misst der Player
              selbst über unseren eigenen Server — auch die Zuschauer im eingebetteten Player.
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              Fehlt der Abschnitt auch morgen noch, bitte {TECHNIK_KONTAKT} Bescheid geben.
            </Box>
            <NurAdmin>
              <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                Technik: GA4-Data-API-Aufruf fehlgeschlagen{' '}
                {data.embed_ab.error && <code>{data.embed_ab.error}</code>} — Service-Account (
                <code>node-app/configs/ga4-service-account.json</code>) oder Kontingent der Property
                281089169 prüfen.
              </Box>
            </NurAdmin>
          </Alert>
        </>
      )}

      {data?.embed_ab && data.embed_ab.source === 'ga4' && data.embed_ab.total_impressions > 0 && (
        <Box mb={3} mt={1}>
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <Typography variant="h6" fontWeight={600}>
              📊 Player auf Finanztrends- und Partnerseiten — zwei Fassungen im Vergleich
            </Typography>
            <Tooltip title="Zahlen aus Google Analytics, ohne Bots, alle 15 Minuten aktualisiert. Erfasst auch Besucher am Handy zuverlässig.">
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Quelle: Google Analytics"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="textSecondary" display="block" mb={2}>
            Während einer Sendung zeigen Finanztrends und die Partnerseiten auf Startseite und
            Artikelseiten einen eingebetteten Player. Zwei Fassungen werden gegeneinander getestet:
            ein festes <strong>Live-Bild</strong> und der echte <strong>Live-Player</strong>. Jeder
            Besucher bekommt per Zufall dauerhaft eine der beiden zu sehen, je zur Hälfte.
          </Typography>
          <NurAdmin>
            <Typography variant="caption" color="textSecondary" display="block" mb={2}>
              Technik: GA4 Widget-Property G-4P0FKHM6HZ, historische Daten bis Juli 2026 aus
              G-Q1CDPZ3BLD (summiert), 15 min Cache. Varianten poster/iframe, Cookie-Bucketing
              50/50.
            </Typography>
          </NurAdmin>

          {data.embed_ab.click_data_source &&
            data.embed_ab.click_data_source !== 'ga4' &&
            data.embed_ab.click_data_trusted_since && (
              <Alert severity="info" sx={{ fontSize: 13, mb: 2 }}>
                <strong>Klick-Zahlen korrigiert.</strong> Vor dem{' '}
                <strong>
                  {new Date(data.embed_ab.click_data_trusted_since).toLocaleDateString('de-DE')}
                </strong>{' '}
                hat Google Analytics auf Handys etwa 10- bis 15-mal zu viele Klicks gezählt (Wischen
                galt als Klick). Für diese Zeit stehen hier deshalb die Besuche, die tatsächlich auf
                live.finanztrends.de ankamen — eher etwas zu niedrig (etwa 5–10 %).{' '}
                {data.embed_ab.click_data_source === 'mixed' && (
                  <>
                    Ab dem{' '}
                    {new Date(data.embed_ab.click_data_trusted_since).toLocaleDateString('de-DE')}{' '}
                    zählen wieder die Klicks aus Google Analytics.
                  </>
                )}
                <NurAdmin>
                  <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                    Technik: Quelle vor dem Stichtag live_viewer_attribution (Server-Landings),
                    danach GA4 (pointerup-Fix in embed.js).
                  </Box>
                </NurAdmin>
              </Alert>
            )}

          {!CLICK_METRICS_TRUSTED && (
            <Alert severity="warning" sx={{ fontSize: 13, mb: 2 }}>
              <strong>Klick-Daten aktuell ausgeblendet.</strong> Untersuchung 2026-06-20 ergab, dass
              die GA4 Klick-Events ca. <strong>10-15× überzählt</strong> sind: der Touch-Handler im
              Embed reagiert auf jeden{' '}
              <Tooltip title="Auf Touch-Geräten beginnt jedes Tippen UND jedes Scrollen mit pointerdown. Wenn der Finger sich danach weiterbewegt → Scroll, kein Klick. Wir haben aber bereits ein 'pointerdown' = Klick registriert.">
                <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                  Touch-Start (pointerdown)
                </span>
              </Tooltip>{' '}
              auf dem Embed — auch wenn der Nutzer nur scrollt und nie wirklich klickt.
              Server-seitiger Vergleich: nur <strong>~73 Personen</strong> sind über 4 Sendungen
              tatsächlich auf die Live-Seite navigiert (statt der 948 GA4-„Klicker“).{' '}
              <strong>Seher-Zahlen sind weiterhin korrekt</strong> (Impressions kommen aus einem
              anderen Code-Pfad ohne Touch-Detektion). Fix in Arbeit: Wechsel von{' '}
              <code>pointerdown</code> → <code>pointerup</code> in <code>embed.js</code> filtert
              Scroll-Touches automatisch raus, weil <code>pointerup</code> nur bei vollendeten Taps
              feuert. Sobald deployed, werden die Klick-Karten + CTR wieder eingeblendet.
            </Alert>
          )}

          {/* Operator warnings — compact */}
          {(data.embed_ab.balance_warning ||
            data.embed_ab.no_clicks_warning ||
            data.embed_ab.small_sample_warning) && (
            <Stack spacing={1} mb={2}>
              {/* Verteilungs- und Messwarnungen sind Technik (Persona-Runde 1: „Bucketing“) —
                  nur der Admin sieht sie; für Marketing ändert sich an den Zahlen nichts. */}
              {data.embed_ab.balance_warning && (
                <NurAdmin>
                  <Alert severity="warning" sx={{ fontSize: 13 }}>
                    <strong>Technik: A/B-Bucketing unausgewogen.</strong> Die 50/50-Aufteilung
                    zwischen poster und iframe ist verzerrt — eine Variante hat unter 20&nbsp;%
                    aller Impressions.
                  </Alert>
                </NurAdmin>
              )}
              {data.embed_ab.no_clicks_warning && (
                <NurAdmin>
                  <Alert severity="warning" sx={{ fontSize: 13 }}>
                    <strong>Technik: keine Klicks erfasst</strong> trotz mehr als 50 Impressions —
                    Click-Tracking prüfen.
                  </Alert>
                </NurAdmin>
              )}
              {data.embed_ab.small_sample_warning &&
                !data.embed_ab.balance_warning &&
                !data.embed_ab.no_clicks_warning && (
                  <Alert severity="info" sx={{ fontSize: 13 }}>
                    Noch wenig Daten ({zahl(data.embed_ab.total_impressions)} Aufrufe). Die Werte
                    werden mit mehr Sendungen aussagekräftiger.
                  </Alert>
                )}
            </Stack>
          )}

          {/* --- Section 1: Per-publisher comparison cards (only when ≥2 publishers) --- */}
          {(() => {
            const rows = data.embed_ab.by_surface_variant;
            const agg = new Map<
              string,
              {
                slug: string;
                label: string;
                role: PublisherRole;
                description: string;
                visitors: number;
                clickers: number;
                visitors_thresholded: boolean;
                clickers_thresholded: boolean;
              }
            >();
            for (const r of rows) {
              const slug = detectPublisher(r.surface);
              const meta = publisherMeta[slug] ?? {
                role: 'partner' as PublisherRole,
                description: '',
              };
              const entry = agg.get(slug) ?? {
                slug,
                label: publisherLabel(slug),
                role: meta.role,
                description: meta.description,
                visitors: 0,
                clickers: 0,
                visitors_thresholded: false,
                clickers_thresholded: false,
              };
              entry.visitors += r.unique_visitors ?? 0;
              entry.clickers += r.unique_clickers ?? 0;
              // Track thresholding so the aggregate doesn't render literal "0"
              // when GA4 redacted the unique-user count of any contributing
              // cell. Example: sw_sidebar got 6 click events from 1-4 actual
              // people → GA4 returned unique_clickers=0 with thresholded=true
              // → naive sum is 0 → card showed "0 Davon geklickt" + "0.0 %"
              // which read as "broken integration". Now propagates "<5" + "–"
              // via renderUnique like the variant cards already do.
              if (r.unique_visitors_thresholded) entry.visitors_thresholded = true;
              if (r.unique_clickers_thresholded) entry.clickers_thresholded = true;
              agg.set(slug, entry);
            }
            const summaries = Array.from(agg.values()).sort((a, b) => b.visitors - a.visitors);
            if (summaries.length < 2) return null;
            return (
              <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Wie schneidet der eingebettete Player je Website ab?
                  </Typography>
                  <Tooltip title="Je Website (eigene und Partnerseiten): wie viele Besucher den Player gesehen haben, wie viele davon geklickt haben und die Klickrate je Person. Zahlen aus Google Analytics.">
                    <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {summaries.map((s) => {
                    // When clickers were thresholded to 0 (1-4 actual users
                    // GA4-redacted) we can't compute a meaningful CTR — denominator
                    // is sound but numerator is "<5" not 0. Render CTR as "<X%"
                    // ceiling instead of "0.0 %" which falsely implies zero clicks.
                    const ctrThresholdedSuppressed =
                      s.clickers_thresholded && s.clickers === 0 && s.visitors > 0;
                    const ctr = s.visitors > 0 ? s.clickers / s.visitors : 0;
                    const ctrCeiling = s.visitors > 0 ? 4 / s.visitors : 0;
                    const isOwn = s.role === 'own';
                    const accentColor = isOwn ? '#1976d2' : '#9c27b0';
                    const bgColor = isOwn ? '#e3f2fd' : '#f3e5f5';
                    return (
                      <Paper
                        key={s.slug}
                        elevation={0}
                        sx={{
                          flex: '1 1 240px',
                          minWidth: 0,
                          p: 2,
                          border: '1px solid #e0e0e0',
                          borderLeft: `4px solid ${accentColor}`,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          mb={0.5}
                          flexWrap="wrap"
                        >
                          {isOwn ? (
                            <LanguageIcon fontSize="small" sx={{ color: accentColor }} />
                          ) : (
                            <HandshakeIcon fontSize="small" sx={{ color: accentColor }} />
                          )}
                          <Typography variant="subtitle1" fontWeight={600}>
                            {s.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={isOwn ? 'Eigene Website' : 'Partner-Website'}
                            sx={{ backgroundColor: bgColor, fontSize: 11 }}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          mb={1.5}
                        >
                          {s.description}
                        </Typography>
                        <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap">
                          <Box>
                            <Tooltip title="Verschiedene Personen, die den Player gesehen haben — je Platz (Startseite, Artikelseite) und Fassung gezählt und addiert. Wer ihn auf der Startseite UND in Artikeln sah, zählt mehrfach; das betrifft etwa 10–15 %.">
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ cursor: 'help' }}
                              >
                                Gesehen (Summe)
                              </Typography>
                            </Tooltip>
                            <Typography variant="h5" fontWeight={700}>
                              {zahl(s.visitors)}
                            </Typography>
                          </Box>
                          {CLICK_METRICS_TRUSTED && (
                            <>
                              <Box>
                                <Tooltip
                                  title={
                                    s.clickers_thresholded && s.clickers === 0
                                      ? '„<5“: Google Analytics zeigt aus Datenschutzgründen keine genaue Zahl unter 5 (es waren 1 bis 4 Personen).'
                                      : 'Verschiedene Personen, die mindestens einmal geklickt haben.'
                                  }
                                >
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ cursor: 'help' }}
                                  >
                                    Davon geklickt
                                  </Typography>
                                </Tooltip>
                                <Typography variant="h5" fontWeight={700}>
                                  {renderUnique(
                                    s.clickers,
                                    s.clickers === 0 && s.clickers_thresholded,
                                  )}
                                </Typography>
                              </Box>
                              <Box>
                                <Tooltip
                                  title={
                                    ctrThresholdedSuppressed
                                      ? `Nicht genau angebbar — Google Analytics nennt weniger als 5 Personen mit Klick (Datenschutz). Höchstens ${formatCtr(ctrCeiling)}.`
                                      : 'Klickrate je Person: Personen mit Klick ÷ Personen, die den Player gesehen haben.'
                                  }
                                >
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ cursor: 'help', display: 'block' }}
                                  >
                                    Klickrate
                                  </Typography>
                                </Tooltip>
                                <Box mt={0.5}>
                                  <Chip
                                    size="medium"
                                    color={ctrThresholdedSuppressed ? 'default' : ctrChipColor(ctr)}
                                    label={
                                      ctrThresholdedSuppressed
                                        ? `<${formatCtr(ctrCeiling)}`
                                        : formatCtr(ctr)
                                    }
                                    sx={{ fontWeight: 700, fontSize: 16 }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                          {!CLICK_METRICS_TRUSTED && (
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ display: 'block' }}
                              >
                                Klick-Metriken
                              </Typography>
                              <Tooltip title="Klicker- und CTR-Daten sind aktuell ~10-15× überzählt (siehe Warnung oben). Werden wieder eingeblendet sobald embed.js auf pointerup umgestellt + deployed ist.">
                                <Chip
                                  size="small"
                                  label="temporär ausgeblendet"
                                  variant="outlined"
                                  sx={{ cursor: 'help', fontSize: 11 }}
                                />
                              </Tooltip>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            );
          })()}

          {/* --- Section 2: A/B variant — bucket balance (Seher) + CTR if trusted --- */}
          <Box mb={3}>
            <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                {CLICK_METRICS_TRUSTED
                  ? 'Test: Welche Fassung schneidet besser ab?'
                  : 'Test: Verteilung der Besucher auf die Fassungen'}
              </Typography>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" display="block" mb={0.5}>
                      <strong>Live-Bild:</strong> {variantTooltip.poster}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>Live-Player:</strong> {variantTooltip.iframe}
                    </Typography>
                    {!CLICK_METRICS_TRUSTED && (
                      <Typography
                        variant="caption"
                        display="block"
                        mt={0.5}
                        sx={{ fontStyle: 'italic' }}
                      >
                        Welche Variante besser performt lässt sich aktuell nicht sagen — Klick-Daten
                        sind ausgeblendet (siehe Warnung oben). Hier wird vorerst nur die
                        Bucket-Verteilung gezeigt: idealerweise 50/50.
                      </Typography>
                    )}
                  </Box>
                }
              >
                <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Stack>
            {(() => {
              const variants = data.embed_ab.by_variant;
              const maxSeher = Math.max(...variants.map((v) => v.unique_visitors), 1);
              const maxCtr = Math.max(...variants.map((v) => v.ctr), 0.0001);
              const totalSeher = variants.reduce((s, v) => s + v.unique_visitors, 0);
              const anyThresholded = variants.some((v) => v.unique_visitors_thresholded);
              return (
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {variants.map((v) => {
                    const ctrByUser =
                      v.unique_visitors > 0 ? v.unique_clickers / v.unique_visitors : 0;
                    const isCtrWinner = v.ctr >= maxCtr - 0.0001 && v.ctr > 0;
                    const sehrShare = totalSeher > 0 ? (v.unique_visitors / totalSeher) * 100 : 0;
                    const winner = CLICK_METRICS_TRUSTED ? isCtrWinner : false;
                    return (
                      <Paper
                        key={v.variant}
                        elevation={0}
                        sx={{
                          flex: '1 1 280px',
                          minWidth: 0,
                          p: 2,
                          border: winner ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                          backgroundColor: '#ffffff',
                          position: 'relative',
                        }}
                      >
                        {winner && variants.length > 1 && (
                          <Chip
                            size="small"
                            color="success"
                            label="🏆 Höchste Klickrate"
                            sx={{ position: 'absolute', top: 8, right: 8, fontWeight: 600 }}
                          />
                        )}
                        <Tooltip title={variantTooltip[v.variant] || ''}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ cursor: 'help' }}>
                            {variantDisplayLabel[v.variant] || v.variant}
                          </Typography>
                        </Tooltip>
                        <Box mt={1} mb={1.5}>
                          <LinearProgress
                            variant="determinate"
                            value={
                              CLICK_METRICS_TRUSTED
                                ? Math.min(100, (v.ctr / maxCtr) * 100)
                                : Math.min(100, (v.unique_visitors / maxSeher) * 100)
                            }
                            sx={{
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: '#f0f0f0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: winner ? '#2e7d32' : '#90caf9',
                              },
                            }}
                          />
                        </Box>
                        <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap">
                          <Box>
                            <Tooltip title="Verschiedene Personen, die diese Fassung gesehen haben. „<5“ = Google Analytics zeigt aus Datenschutzgründen keine genaue Zahl (1 bis 4).">
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ cursor: 'help' }}
                              >
                                Gesehen
                              </Typography>
                            </Tooltip>
                            <Typography variant="h6" fontWeight={700}>
                              {renderUnique(v.unique_visitors, v.unique_visitors_thresholded)}
                            </Typography>
                          </Box>
                          <Box>
                            <Tooltip title="Anteil dieser Fassung an allen Personen, die den Player gesehen haben — gedacht ist halb und halb. „–“ bei sehr wenig Daten, weil Google Analytics kleine Zahlen nicht genau nennt.">
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ cursor: 'help' }}
                              >
                                Anteil
                              </Typography>
                            </Tooltip>
                            <Typography variant="h6" fontWeight={700}>
                              {anyThresholded || totalSeher === 0 ? '–' : `${zahl(sehrShare, 1)} %`}
                            </Typography>
                          </Box>
                          {CLICK_METRICS_TRUSTED && (
                            <>
                              <Box>
                                <Tooltip title="Klicks ÷ Aufrufe des Players. Auch dann verlässlich, wenn Google Analytics beide Zahlen zu hoch zählt — der Fehler kürzt sich heraus.">
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ cursor: 'help' }}
                                  >
                                    Klickrate je Aufruf
                                  </Typography>
                                </Tooltip>
                                <Typography variant="h6" fontWeight={700}>
                                  {formatPercent(v.ctr)}
                                </Typography>
                              </Box>
                              <Box>
                                <Tooltip title="Personen mit Klick ÷ Personen, die den Player gesehen haben.">
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ cursor: 'help' }}
                                  >
                                    Klickrate je Person
                                  </Typography>
                                </Tooltip>
                                <Typography variant="h6" fontWeight={700}>
                                  {formatCtr(ctrByUser)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  Personen mit Klick
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {renderUnique(v.unique_clickers, v.unique_clickers_thresholded)}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              );
            })()}
          </Box>

          {/* --- Section 3: Per-placement bar chart --- */}
          {(() => {
            const rows = data.embed_ab.by_surface_variant;
            const agg = new Map<
              string,
              {
                placement: string;
                label: string;
                poster_visitors: number;
                iframe_visitors: number;
                poster_clickers: number;
                iframe_clickers: number;
              }
            >();
            for (const r of rows) {
              const place = placementBareKey(r.surface);
              const entry = agg.get(place) ?? {
                placement: place,
                label: placementShortLabel(place),
                poster_visitors: 0,
                iframe_visitors: 0,
                poster_clickers: 0,
                iframe_clickers: 0,
              };
              const v = r.unique_visitors ?? 0;
              const c = r.unique_clickers ?? 0;
              if (r.variant === 'poster') {
                entry.poster_visitors += v;
                entry.poster_clickers += c;
              } else if (r.variant === 'iframe') {
                entry.iframe_visitors += v;
                entry.iframe_clickers += c;
              }
              agg.set(place, entry);
            }
            const items = Array.from(agg.values())
              .map((e) => ({
                ...e,
                poster_ctr:
                  e.poster_visitors > 0 ? (e.poster_clickers / e.poster_visitors) * 100 : 0,
                iframe_ctr:
                  e.iframe_visitors > 0 ? (e.iframe_clickers / e.iframe_visitors) * 100 : 0,
              }))
              .sort(
                (a, b) =>
                  PLACEMENT_ORDER.indexOf(a.placement) - PLACEMENT_ORDER.indexOf(b.placement),
              );
            if (items.length === 0) return null;
            const chartData = CLICK_METRICS_TRUSTED
              ? items.map((i) => ({
                  placement: i.label,
                  'Live-Bild': Math.round(i.poster_ctr * 10) / 10,
                  'Live-Player': Math.round(i.iframe_ctr * 10) / 10,
                }))
              : items.map((i) => ({
                  placement: i.label,
                  'Live-Bild': i.poster_visitors,
                  'Live-Player': i.iframe_visitors,
                }));
            const chartTitle = CLICK_METRICS_TRUSTED
              ? 'Welche Platzierung bringt die meisten Klicks?'
              : 'Welche Platzierung erreicht die meisten Personen?';
            const chartTooltip = CLICK_METRICS_TRUSTED
              ? 'Klickrate (Personen mit Klick je Person, die den Player sah, in %) je Platz auf der Website. Beide Fassungen nebeneinander, über alle Websites zusammengezählt.'
              : 'Personen, die den Player gesehen haben, je Platz auf der Website, über alle Websites zusammengezählt. Wer mehrere Plätze sah, zählt mehrfach.';
            const axisLegend = CLICK_METRICS_TRUSTED ? 'Klickrate in %' : 'Gesehen (Summe)';
            const valueFmt = CLICK_METRICS_TRUSTED
              ? (v: number | string) => `${zahl(Number(v), 1)} %`
              : (v: number | string) => zahl(Number(v));
            const footnote = CLICK_METRICS_TRUSTED
              ? 'Klickrate = Personen mit Klick ÷ Personen, die den Player gesehen haben (je Person, nicht je Klick).'
              : 'Gesehen (Summe) = verschiedene Personen je Platz und Fassung, über alle Websites addiert. Wer mehrere Plätze sah, zählt mehrfach (etwa 10–15 %).';
            return (
              <Box mb={3}>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {chartTitle}
                  </Typography>
                  <Tooltip title={chartTooltip}>
                    <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                  </Tooltip>
                </Stack>
                <Paper
                  elevation={0}
                  sx={{ p: 2, border: '1px solid #e0e0e0', backgroundColor: '#ffffff' }}
                >
                  <Box sx={{ height: 280 }}>
                    <ResponsiveBar
                      data={chartData}
                      keys={['Live-Bild', 'Live-Player']}
                      indexBy="placement"
                      margin={{ top: 16, right: 120, bottom: 60, left: 70 }}
                      padding={0.3}
                      groupMode="grouped"
                      colors={['#42a5f5', '#66bb6a']}
                      axisBottom={{ tickRotation: -15 }}
                      axisLeft={{
                        legend: axisLegend,
                        legendPosition: 'middle',
                        legendOffset: -55,
                      }}
                      labelSkipHeight={12}
                      labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      valueFormat={valueFmt}
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'bottom-right',
                          direction: 'column',
                          translateX: 100,
                          itemWidth: 90,
                          itemHeight: 18,
                          symbolSize: 12,
                        },
                      ]}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {footnote}
                  </Typography>
                </Paper>
              </Box>
            );
          })()}

          {/* --- Section 4: Detail table — collapsed by default for marketing --- */}
          <Accordion sx={{ mb: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>
                📋 Alle Zahlen je Platz, Website und Fassung
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {data.embed_ab.surface_data_quality === 'inflated' && (
                <Alert severity="info" sx={{ fontSize: 13, mb: 1.5 }}>
                  <strong>Hinweis zu den absoluten Zahlen:</strong> Die <em>Aufrufe</em>
                  {CLICK_METRICS_TRUSTED && (
                    <>
                      {' '}
                      und <em>Klicks</em>
                    </>
                  )}{' '}
                  sind etwa {zahl(data.embed_ab.surface_inflation_factor || 0, 1)}-mal zu hoch
                  (bekannter Zählfehler von Google Analytics bei dieser Aufschlüsselung). Die
                  Spalten <strong>Anteil</strong> und <strong>Gesehen</strong>
                  {CLICK_METRICS_TRUSTED && (
                    <>
                      , <strong>Personen mit Klick</strong> und <strong>Klickrate</strong>
                    </>
                  )}{' '}
                  stimmen.
                </Alert>
              )}
              {(() => {
                const allRows = data.embed_ab.by_surface_variant;
                const availablePubs = Array.from(
                  new Set(allRows.map((r) => detectPublisher(r.surface))),
                )
                  .map((slug) => ({ slug, label: publisherLabel(slug) }))
                  .sort((a, b) => a.label.localeCompare(b.label));
                if (availablePubs.length <= 1) return null;
                return (
                  <Stack direction="row" spacing={2} alignItems="center" mb={1.5}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="publisher-filter-label">Website filtern</InputLabel>
                      <Select
                        labelId="publisher-filter-label"
                        label="Website filtern"
                        value={publisherFilter}
                        onChange={(e) => setPublisherFilter(e.target.value)}
                      >
                        <MenuItem value="all">Alle Websites ({availablePubs.length})</MenuItem>
                        {availablePubs.map((p) => (
                          <MenuItem key={p.slug} value={p.slug}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                );
              })()}
              <TableContainer
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  overflowX: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        <Tooltip title="Wo der Player stand — Hauptbanner der Startseite oder Seitenleiste der Artikelseite, dazu das Gerät.">
                          <span style={{ cursor: 'help' }}>Platz</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        <Tooltip title="Welche Website den Player gezeigt hat.">
                          <span style={{ cursor: 'help' }}>Website</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                        <Tooltip
                          title={`Fassung im Test. Live-Bild: ${variantTooltip.poster} — Live-Player: ${variantTooltip.iframe}`}
                        >
                          <span style={{ cursor: 'help' }}>Fassung</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                      >
                        <Tooltip title="Wie oft der Player gezeigt wurde. Wenn Google Analytics zu hoch zählt, etwa doppelt — die Spalten Anteil und Klickrate stimmen trotzdem.">
                          <span style={{ cursor: 'help' }}>
                            Aufrufe
                            {data.embed_ab.surface_data_quality === 'inflated' && ' (etwa 2-mal)'}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                      >
                        <Tooltip title="Anteil dieser Zeile an allen Aufrufen des Players. Stimmt immer.">
                          <span style={{ cursor: 'help' }}>Anteil</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                      >
                        <Tooltip title="Verschiedene Personen, die den Player gesehen haben. „<5“ = Google Analytics nennt aus Datenschutzgründen keine genaue Zahl (1 bis 4).">
                          <span style={{ cursor: 'help' }}>Gesehen</span>
                        </Tooltip>
                      </TableCell>
                      {CLICK_METRICS_TRUSTED && (
                        <>
                          <TableCell
                            align="right"
                            sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                          >
                            <Tooltip title="Alle Klicks (eine Person kann mehrfach klicken). Wenn Google Analytics zu hoch zählt, etwa doppelt.">
                              <span style={{ cursor: 'help' }}>
                                Klicks
                                {data.embed_ab.surface_data_quality === 'inflated' &&
                                  ' (etwa 2-mal)'}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                          >
                            <Tooltip title="Verschiedene Personen, die mindestens einmal geklickt haben. „<5“ = keine genaue Zahl aus Datenschutzgründen (1 bis 4).">
                              <span style={{ cursor: 'help' }}>Personen mit Klick</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
                          >
                            <Tooltip title="Klickrate = Klicks ÷ Aufrufe. Stimmt immer — ein Zählfehler kürzt sich heraus.">
                              <span style={{ cursor: 'help' }}>Klickrate</span>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                      {!CLICK_METRICS_TRUSTED && (
                        <TableCell
                          align="right"
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            fontWeight: 'bold',
                            color: '#888',
                          }}
                        >
                          <Tooltip title="Klicks / Klicker / CTR sind temporär ausgeblendet (siehe Warnung oben). Werden nach embed.js pointerup-Fix wieder eingeblendet.">
                            <span style={{ cursor: 'help', fontStyle: 'italic' }}>
                              Klick-Spalten (ausgeblendet)
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.embed_ab.by_surface_variant
                      .filter(
                        (row) =>
                          publisherFilter === 'all' ||
                          detectPublisher(row.surface) === publisherFilter,
                      )
                      .map((row) => (
                        <TableRow key={`${row.surface}|${row.variant}`} hover>
                          <TableCell>
                            <Tooltip title={istAdmin ? `Technik: ${row.surface}` : ''}>
                              <span style={{ cursor: 'help' }}>
                                {surfaceDisplayLabel(row.surface)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{publisherLabel(detectPublisher(row.surface))}</TableCell>
                          <TableCell>
                            <Tooltip title={variantTooltip[row.variant] || ''}>
                              <span style={{ cursor: 'help' }}>
                                {variantShortLabel[row.variant] || row.variant}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            {data.embed_ab!.surface_data_quality === 'inflated' ? '~' : ''}
                            {zahl(row.impressions)}
                          </TableCell>
                          <TableCell align="right">
                            {row.impression_share_pct !== undefined
                              ? `${zahl(row.impression_share_pct, 1)} %`
                              : '–'}
                          </TableCell>
                          <TableCell align="right">
                            {renderUnique(row.unique_visitors, row.unique_visitors_thresholded)}
                          </TableCell>
                          {CLICK_METRICS_TRUSTED && (
                            <>
                              <TableCell align="right">
                                {data.embed_ab!.surface_data_quality === 'inflated' ? '~' : ''}
                                {zahl(row.clicks)}
                              </TableCell>
                              <TableCell align="right">
                                {renderUnique(row.unique_clickers, row.unique_clickers_thresholded)}
                              </TableCell>
                              <TableCell align="right">{formatPercent(row.ctr)}</TableCell>
                            </>
                          )}
                          {!CLICK_METRICS_TRUSTED && (
                            <TableCell align="right" sx={{ color: '#bbb' }}>
                              —
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* --- Section 5: Glossar — collapsed --- */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HelpOutlineIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Was bedeuten die Begriffe?
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Was ist der eingebettete Player?
                  </Typography>
                  <Typography variant="body2">
                    Ein Live-Player, der während einer Sendung auf finanztrends.de und den
                    Partner-Websites (z. B. boerse-global.de) erscheint — im Hauptbanner der
                    Startseite oder in der Seitenleiste von Artikeln.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Live-Bild gegen Live-Player
                  </Typography>
                  <Typography variant="body2">
                    <strong>Live-Bild:</strong> {variantTooltip.poster}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Live-Player:</strong> {variantTooltip.iframe}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Beide Fassungen laufen als Test nebeneinander: jeder Besucher bekommt per Zufall
                    eine davon und behält sie, je zur Hälfte.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Aufrufe, Gesehen, Klicks, Personen mit Klick
                  </Typography>
                  <Typography variant="body2">
                    <strong>Aufrufe:</strong> wie oft der Player insgesamt gezeigt wurde (eine
                    Person kann mehrere auslösen, z. B. auf mehreren Artikeln).
                    <br />
                    <strong>Gesehen:</strong> verschiedene Personen, die den Player gesehen haben —
                    jede zählt einmal, egal wie oft.
                    <br />
                    <strong>Klicks:</strong> alle Klicks (eine Person kann mehrfach klicken).
                    <br />
                    <strong>Personen mit Klick:</strong> verschiedene Personen, die mindestens
                    einmal geklickt haben.
                    <br />
                    <strong>Klickrate:</strong> Klicks ÷ Aufrufe bzw. Personen mit Klick ÷ Gesehen.
                    Höher = mehr Interesse.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Was bedeutet „&lt;5“?
                  </Typography>
                  <Typography variant="body2">
                    Google Analytics nennt aus Datenschutzgründen keine genauen Personenzahlen unter
                    5. „&lt;5“ heißt: es waren 1 bis 4 Personen.
                  </Typography>
                </Box>
                {data.embed_ab.surface_data_quality === 'inflated' && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Warum steht vor Aufrufen und Klicks manchmal ein „~“?
                      </Typography>
                      <Typography variant="body2">
                        Google Analytics zählt Aufrufe und Klicks etwa{' '}
                        {zahl(data.embed_ab.surface_inflation_factor || 2, 1)}-mal zu hoch, wenn
                        nach Platz aufgeschlüsselt wird. <strong>Klickrate</strong>,{' '}
                        <strong>Anteil</strong> und alle <strong>Personenzahlen</strong> (Gesehen,
                        Personen mit Klick) stimmen trotzdem — der Fehler kürzt sich heraus.
                      </Typography>
                    </Box>
                  </>
                )}
                {!CLICK_METRICS_TRUSTED && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Warum sind Klicker, CTR & „Welche Variante schlägt sich besser“ gerade
                        ausgeblendet?
                      </Typography>
                      <Typography variant="body2">
                        Bei einer Untersuchung am 2026-06-20 ist aufgefallen, dass die Klick-Events
                        aus GA4 ca. <strong>10-15× überzählt</strong> sind. Grund: der Touch-Handler
                        im Embed nutzt das Browser-Event <code>pointerdown</code>, das auf
                        Touch-Geräten am Anfang jeder Berührung feuert — auch wenn der Nutzer nur
                        scrollen will (und nie wirklich klickt). Wir haben das mit Server-Tracking
                        gegengeprüft: über 4 Sendungen sind nur ~73 Personen tatsächlich auf die
                        Live-Seite navigiert, GA4 zählt aber 948 „Klicker“.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Bis der Fix deployed ist (Wechsel von <code>pointerdown</code> →{' '}
                        <code>pointerup</code>, das nur bei vollendeten Taps feuert), zeigen wir nur
                        die Seher-Zahlen (die sind unbeeinflusst). Die Bucket-Verteilung (Poster vs
                        Iframe Seher-Anteil) bleibt sichtbar, weil A/B-Bucketing unabhängig vom
                        Touch-Tracking ist.
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Watch-Engagement Funnel — per-broadcast engagement of the iframe-inline
          embed cohort + click-throughs. Data from live_viewer_attribution
          (UPSERT'd by /heartbeat). Sessions BEFORE the 2026-06-20 deploy show
          a "Daten ab dieser Sendung verfügbar" hint instead of misleading
          zero-engagement numbers. */}
      {sessionsAsc.length > 0 && data?.sessions?.some((s) => s.engagement) && (
        <Box mb={3}>
          <Stack direction="row" alignItems="center" spacing={0.5} mb={1} mt={1}>
            <Typography variant="h6" fontWeight={600}>
              👁️ Wer schaut wie lange? — je Sendung
            </Typography>
          </Stack>
          <Box
            sx={{
              backgroundColor: '#f5f7fa',
              border: '1px solid #d0d7e2',
              borderRadius: 1,
              p: 1.5,
              mb: 2,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Kurz erklärt — zwei verschiedene Arten zu zählen:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • <strong>Höchststand</strong> (Kacheln oben, Grafik und Tabelle unten): die meisten
              Zuschauer <em>gleichzeitig</em> in einem Moment. „Höchststand 152“ heißt nicht 152
              Personen insgesamt, sondern 152 im selben Moment.
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • <strong>Verschiedene Zuschauer</strong> (diese Tabelle): Personen{' '}
              <em>über die ganze Sendung</em> gezählt, jede einmal — auch wer 20 Minuten schaut.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
              Zwei Gruppen, getrennt gezählt:
            </Typography>
            <Typography variant="body2">
              • <strong>Im eingebetteten Player</strong>: schaut direkt im Player auf einer Artikel-
              oder Startseite von Finanztrends oder einer Partnerseite, ohne live.finanztrends.de zu
              öffnen.
              <br />• <strong>Auf live.finanztrends.de</strong>: hat die Seite selbst geöffnet — per
              Klick auf den eingebetteten Player oder direkt.
            </Typography>
          </Box>
          <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
            Passt die Tabelle nicht in die Breite, lässt sie sich seitlich verschieben (wischen oder
            die Leiste darunter) — rechts folgen die Spalten zu live.finanztrends.de und zum
            Werbevideo.
          </Typography>
          <TableContainer
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 1,
              border: '1px solid #e0e0e0',
              // Reicht die Breite trotz vollem Tab nicht (Laptop, 17 Spalten),
              // scrollt die Tabelle in sich statt die Seite zu sprengen.
              overflowX: 'auto',
            }}
          >
            {/* nowrap auf Zellenebene: sonst quetschen sich die Kopfzeilen in
                zwei bis drei Zeilen, statt die Tabelle scrollen zu lassen.
                Die Sendungs-Spalte nimmt ihre Umbrueche unten explizit zurueck. */}
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      verticalAlign: 'bottom',
                    }}
                  >
                    Sendung
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={6}
                    sx={{
                      backgroundColor: '#e3f2fd !important',
                      fontWeight: 'bold',
                      borderRight: '2px solid #90caf9',
                    }}
                  >
                    Im eingebetteten Player (Finanztrends- und Partnerseiten)
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={6}
                    sx={{
                      backgroundColor: '#fff3e0 !important',
                      fontWeight: 'bold',
                      borderRight: '2px solid #ffb74d',
                    }}
                  >
                    Auf live.finanztrends.de
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={4}
                    sx={{ backgroundColor: '#e8f5e9 !important', fontWeight: 'bold' }}
                  >
                    Werbevideo nach der Sendung
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e3f2fd !important', fontWeight: 'bold' }}
                    title="Verschiedene Personen, die die Sendung im eingebetteten Player gesehen haben — über die ganze Sendung gezählt, jede einmal. Nicht gleichzeitig: der Höchststand in der Tabelle unten ist meist deutlich kleiner."
                  >
                    Verschiedene Zuschauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e3f2fd !important', fontWeight: 'bold' }}
                    title="Aufteilung nach Breite: Handy = unter 600 Pixel, Desktop = ab 600 Pixel. Gemessen wird hier die Breite des eingebetteten Players, nicht des Geräts — ein schmaler Platz auf dem Desktop zählt als Handy. Guter Näherungswert. Erhoben seit 01.06.2026."
                  >
                    Handy / Desktop
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e3f2fd !important', fontWeight: 'bold' }}
                    title="Davon mindestens 30 Sekunden dabei"
                  >
                    ≥ 30 s
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e3f2fd !important', fontWeight: 'bold' }}
                    title="Davon mindestens 1 Minute dabei"
                  >
                    ≥ 1 Min.
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e3f2fd !important', fontWeight: 'bold' }}
                    title="Davon mindestens 5 Minuten dabei"
                  >
                    ≥ 5 Min.
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: '#e3f2fd !important',
                      fontWeight: 'bold',
                      borderRight: '2px solid #90caf9',
                    }}
                    title="Durchschnittliche Sehdauer im eingebetteten Player (Minuten:Sekunden)"
                  >
                    Ø Sehdauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#fff3e0 !important', fontWeight: 'bold' }}
                    title="Verschiedene Personen, die live.finanztrends.de geöffnet haben (per Klick auf den eingebetteten Player oder direkt) — über die ganze Sendung, jede einmal. Nicht gleichzeitig: den Höchststand je Moment zeigt die Spalte daneben."
                  >
                    Verschiedene Zuschauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#fff3e0 !important', fontWeight: 'bold' }}
                    title="Aufteilung nach Breite: Handy = unter 600 Pixel, Desktop = ab 600 Pixel. Auf live.finanztrends.de ist das das echte Browserfenster, also genauer als beim eingebetteten Player. Erhoben seit 01.06.2026."
                  >
                    Handy / Desktop
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#fff3e0 !important', fontWeight: 'bold' }}
                    title={HOECHST_SEITE_TIPP}
                  >
                    Höchststand gleichzeitig
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#fff3e0 !important', fontWeight: 'bold' }}
                    title="Durchschnittliche Sehdauer auf live.finanztrends.de (Minuten:Sekunden) — Abstand zwischen erster und letzter Meldung des Players je Person."
                  >
                    Ø Sehdauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#fff3e0 !important', fontWeight: 'bold' }}
                    title="Personen, die auf den eingebetteten Player geklickt haben und so auf live.finanztrends.de kamen. Nicht dasselbe wie „Klicks“ in der Tabelle unten — das sind Klicks auf Einblendungen und den Knopf im Werbevideo."
                  >
                    davon per Klick auf den Player
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: '#fff3e0 !important',
                      fontWeight: 'bold',
                      borderRight: '2px solid #ffb74d',
                    }}
                    title="Direkt aufgerufen (Adresse eingetippt, Lesezeichen, Link aus einer Mail) — nicht über den eingebetteten Player"
                  >
                    davon direkt
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e8f5e9 !important', fontWeight: 'bold' }}
                    title="Verschiedene Personen, die nach dem Umschalten auf das Werbevideo weitergeschaut haben — jede einmal. Die Zelle zeigt beim Darüberfahren die Aufteilung. Ein vorangestelltes ~ heißt: geschätzt, weil die Sendung vor dem 14.08.2026 lief."
                  >
                    Zuschauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e8f5e9 !important', fontWeight: 'bold' }}
                    title="Durchschnittliche Sehdauer während des Werbevideos (ab dem Umschalten gerechnet, nicht ab Sendungsbeginn)."
                  >
                    Ø Sehdauer
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e8f5e9 !important', fontWeight: 'bold' }}
                    title="Klicks auf den Knopf WÄHREND des Werbevideos nach der Sendung. Klicks auf Einblendungen während der Sendung stehen in der Sendungs-Tabelle unten."
                  >
                    Klicks
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ backgroundColor: '#e8f5e9 !important', fontWeight: 'bold' }}
                    title="Bestellungen (Anfos), die aus einem Klick dieser Sendung (Einblendung oder Knopf im Werbevideo) entstanden sind. Ein vorangestelltes ~ heißt geschätzt: zugeordnet über die an diesem Sendetag geklickten Links. Seit dem 14.08.2026 trägt jeder Link ein Sendungs-Kennzeichen, ab dann ist die Zahl exakt. Ein Strich heißt: Bestelldatenbank nicht erreichbar — nicht null."
                  >
                    Anfos
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessionsAsc
                  .slice()
                  .reverse()
                  .map((s) => {
                    const eng = s.engagement;
                    if (!eng) return null;
                    // Sendungs-Zelle: Name aus dem Schedule über Datum+Uhrzeit.
                    // started_at ist Berlin-Wallclock (geschrieben mit
                    // berlinNow) — moment.utc liest die Bytes unverfälscht,
                    // blankes moment() verschiebt um die Browser-Zone.
                    const showCell = (
                      // Einzige Spalte, die umbrechen darf — ein langer
                      // Aufreisser wuerde die Tabelle sonst unnoetig breit ziehen.
                      //
                      // '&&' verdoppelt die Klasse und hebt die Spezifitaet auf
                      // (0,2,0). Ohne das gewinnt die Tabellen-Regel '& td'
                      // (0,1,1) und die Zelle bleibt nowrap — dann laeuft ein
                      // langer Name quer ueber die Nachbarspalten (gemessen
                      // 2026-08-14: 324px Inhalt in einer 250px-Zelle).
                      <TableCell
                        sx={{
                          '&&': { whiteSpace: 'normal' },
                          minWidth: 170,
                          maxWidth: 250,
                          wordBreak: 'break-word',
                        }}
                      >
                        {s.show_name ? (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                              {s.show_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {moment.utc(s.started_at).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          </>
                        ) : (
                          moment.utc(s.started_at).format('DD.MM.YYYY HH:mm')
                        )}
                      </TableCell>
                    );
                    if (!eng.has_engagement_data) {
                      return (
                        <TableRow key={s.id} hover>
                          {showCell}
                          <TableCell align="right">
                            {eng.iframe_inline_total.toLocaleString('de-DE')}
                          </TableCell>
                          <TableCell
                            colSpan={15}
                            align="center"
                            sx={{ color: '#888', fontSize: 12 }}
                          >
                            <em>Sehdauer und Herkunft werden erst seit dem 20.06.2026 gemessen</em>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const pct = (num: number, denom: number) =>
                      denom > 0 ? ` (${Math.round((num / denom) * 100)}%)` : '';
                    // Ein Strich statt "0 / 0", solange viewport_class für die
                    // Sendung gar nicht erhoben wurde — sonst liest sich eine
                    // Messlücke wie ein Messergebnis.
                    const deviceCell = (mobile: number, desktop: number, tip: string) =>
                      eng.has_viewport_data ? (
                        <Tooltip title={tip}>
                          <span>
                            {mobile.toLocaleString('de-DE')}
                            <Typography component="span" variant="caption" color="textSecondary">
                              {' / '}
                            </Typography>
                            {desktop.toLocaleString('de-DE')}
                          </span>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          –
                        </Typography>
                      );
                    const ar = s.auto_roll_engagement;
                    const anf = s.anfos;
                    return (
                      <TableRow key={s.id} hover>
                        {showCell}
                        <TableCell align="right">
                          <strong>{eng.iframe_inline_total.toLocaleString('de-DE')}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {deviceCell(
                            eng.iframe_mobile,
                            eng.iframe_desktop,
                            `Handy ${zahl(eng.iframe_mobile)} (Ø Sehdauer ${formatWatch(eng.iframe_mobile_avg_watch_seconds)}) · Desktop ${zahl(eng.iframe_desktop)} (Ø Sehdauer ${formatWatch(eng.iframe_desktop_avg_watch_seconds)})`,
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {eng.iframe_engaged_30s.toLocaleString('de-DE')}
                          <Typography variant="caption" color="textSecondary">
                            {pct(eng.iframe_engaged_30s, eng.iframe_inline_total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {eng.iframe_engaged_60s.toLocaleString('de-DE')}
                          <Typography variant="caption" color="textSecondary">
                            {pct(eng.iframe_engaged_60s, eng.iframe_inline_total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {eng.iframe_engaged_5min.toLocaleString('de-DE')}
                          <Typography variant="caption" color="textSecondary">
                            {pct(eng.iframe_engaged_5min, eng.iframe_inline_total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderRight: '2px solid #90caf9' }}>
                          {formatWatch(eng.iframe_avg_watch_seconds)}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{eng.standalone_unique_total.toLocaleString('de-DE')}</strong>
                        </TableCell>
                        <TableCell align="right">
                          {deviceCell(
                            eng.standalone_mobile,
                            eng.standalone_desktop,
                            `Handy ${zahl(eng.standalone_mobile)} · Desktop ${zahl(eng.standalone_desktop)}`,
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {eng.standalone_peak_simultaneous.toLocaleString('de-DE')}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          {formatWatch(eng.standalone_avg_watch_seconds)}
                        </TableCell>
                        <TableCell align="right">
                          {eng.click_through_total.toLocaleString('de-DE')}
                          {eng.click_through_engaged_30s > 0 && (
                            <Typography variant="caption" color="textSecondary">
                              {' '}
                              ({zahl(eng.click_through_engaged_30s)} ≥ 30 s)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ borderRight: '2px solid #ffb74d' }}>
                          {eng.direct_total.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right">
                          {ar == null ? (
                            <Typography variant="caption" color="textSecondary">
                              kein Werbevideo
                            </Typography>
                          ) : !ar.available ? (
                            <Tooltip title="Sendung liegt vor Beginn der Zuschauer-Messung (20.06.2026) — darüber ist keine Aussage möglich. Das ist keine Null.">
                              <span>–</span>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                ar.exact
                                  ? `Eingebetteter Player ${zahl(ar.embed)} · live.finanztrends.de ${zahl(ar.standalone)} · davon ≥ 30 s ${zahl(ar.engaged_30s ?? 0)}, ≥ 1 Min. ${zahl(ar.engaged_60s ?? 0)}, ≥ 5 Min. ${zahl(ar.engaged_5min ?? 0)}`
                                  : 'Geschätzt: Zuschauer auf live.finanztrends.de, deren letzte Player-Meldung in die Zeit des Werbevideos fällt (Sendung vor der genauen Messung, erste genaue Sendung 17.08.2026). Der eingebettete Player zeigt kein Werbevideo und zählt deshalb nicht. Abweichung meist um 1 Zuschauer, eher zu niedrig.'
                              }
                            >
                              <strong>
                                {ar.exact ? '' : '~'}
                                {ar.total.toLocaleString('de-DE')}
                              </strong>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {ar == null || !ar.available ? '–' : formatWatch(ar.avg_watch_seconds)}
                        </TableCell>
                        <TableCell align="right">
                          {s.cta_clicks_auto_roll.toLocaleString('de-DE')}
                          {ar != null &&
                            ar.available &&
                            ar.total > 0 &&
                            s.cta_clicks_auto_roll > 0 && (
                              <Typography variant="caption" color="textSecondary">
                                {pct(s.cta_clicks_auto_roll, ar.total)}
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell align="right">
                          {anf == null || !anf.available ? (
                            <Tooltip title="Bestelldatenbank war beim Laden nicht erreichbar — das ist keine Null.">
                              <span>–</span>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title={
                                anf.exact
                                  ? 'Exakt zugeordnet über das Sendungs-Kennzeichen im geklickten Link.'
                                  : 'Geschätzt: Bestellungen am Sendetag über einen in dieser Sendung geklickten Link. Untererfasst, weil nicht jeder Link einen Banner trägt.'
                              }
                            >
                              <strong>
                                {anf.exact ? '' : '~'}
                                {anf.count.toLocaleString('de-DE')}
                              </strong>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Der Player meldet sich alle 15 Sekunden bei unserem Server. <strong>≥ 30 s</strong> =
            mindestens 2 Meldungen, <strong>≥ 1 Min.</strong> = mindestens 4,{' '}
            <strong>≥ 5 Min.</strong> = mindestens 20. Sehdauer = Abstand zwischen erster und
            letzter Meldung. Diese Zahlen kommen von unserem eigenen Server, nicht aus Google
            Analytics.
            <br />
            <strong>~</strong> vor einer Zahl = geschätzt, nicht gemessen. Betrifft Werbevideo-
            Zuschauer und Anfos bei Sendungen vor dem 14.08.2026; ab dann misst der Player beides
            direkt. <strong>Handy / Desktop</strong> trennt nach Breite (Grenze 600 Pixel) — beim
            eingebetteten Player ist das die Breite des Players, nicht die des Geräts.
          </Typography>
        </Box>
      )}

      {sessionsAsc.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
          <Paper
            elevation={0}
            sx={{ flex: 1, p: 2, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}
          >
            <Typography variant="subtitle2" mb={1}>
              {HOECHST_ALLE} je Sendung (gleichzeitig)
            </Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveLine
                data={peakLine}
                margin={{ top: 16, right: 24, bottom: 50, left: 40 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 'auto' }}
                axisBottom={{ tickRotation: -30 }}
                axisLeft={{ tickValues: 5 }}
                colors={['#1976d2']}
                pointSize={6}
                useMesh
                enableSlices="x"
                lineWidth={2}
                animate={false}
              />
            </Box>
          </Paper>
          <Paper
            elevation={0}
            sx={{ flex: 1, p: 2, backgroundColor: '#ffffff', border: '1px solid #e0e0e0' }}
          >
            <Typography variant="subtitle2" mb={1}>
              Klicks je Sendung
            </Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveBar
                data={clicksBar}
                keys={['Klicks']}
                indexBy="date"
                margin={{ top: 16, right: 24, bottom: 50, left: 40 }}
                padding={0.3}
                colors={['#388e3c']}
                axisBottom={{ tickRotation: -30 }}
                axisLeft={{ tickValues: 5 }}
                animate={false}
              />
            </Box>
          </Paper>
        </Stack>
      )}

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}
      >
        Diese Tabelle zeigt je Sendung den <strong>Höchststand</strong> (die meisten Zuschauer
        gleichzeitig) und die Klicks auf Einblendungen und den Knopf im Werbevideo. Wie viele{' '}
        <strong>verschiedene Zuschauer</strong> es über die ganze Sendung waren, steht in der
        Tabelle „Wer schaut wie lange?“ darüber. Fährt man über einen Spaltenkopf, erklärt er sich.
      </Typography>
      <TableContainer
        // overflowX ist MUI-Vorgabe, hier ausdrücklich: die Tabelle scrollt in ihrer Karte.
        sx={{
          backgroundColor: '#ffffff',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          overflowX: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Sendung
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                Dauer
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip
                  title={`${HOECHST_ALLE_TIPP} In Klammern: Höchststand während des Werbevideos danach.`}
                >
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                    {HOECHST_ALLE}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip title="Klicks auf Einblendungen während der Sendung und auf den Knopf im Werbevideo danach (in Klammern: Sendung / Werbevideo). Nicht dasselbe wie „davon per Klick auf den Player“ in der Tabelle darüber.">
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>Klicks</span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip title="Klicks ÷ Höchststand aller Player. Bezogen auf den Höchststand (nicht auf alle Personen), weil ein Knopf immer nur zeitweise zu sehen ist.">
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                    Klicks je Zuschauer
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}>
                <Tooltip title="Wie viel vom Werbevideo nach der Sendung gelaufen ist, gemessen an seiner Länge">
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                    Werbevideo abgespielt
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="right"
                sx={{ backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' }}
              >
                <Tooltip title="Wie viele Zuschauer im Lauf der Sendung gleichzeitig da waren — als Grafik">
                  <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                    Zuschauerverlauf
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.sessions || []).length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Keine Sendungen im gewählten Zeitraum.
                </TableCell>
              </TableRow>
            )}
            {(data?.sessions || []).map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>
                  Live vom {moment.utc(s.started_at).format('DD.MM.YYYY')} um{' '}
                  {moment.utc(s.started_at).format('HH:mm')} Uhr
                </TableCell>
                <TableCell>{formatDuration(s.duration_seconds)}</TableCell>
                <TableCell align="right">
                  {s.peak_viewer_count ? zahl(s.peak_viewer_count) : '–'}
                  {s.auto_roll && (
                    <Tooltip
                      title={`Höchststand während des Werbevideos: ${zahl(s.auto_roll.peak_viewer_count)}`}
                    >
                      <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: 11 }}>
                        ({zahl(s.auto_roll.peak_viewer_count)})
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {zahl(s.cta_clicks)}
                  {s.auto_roll && (
                    <Tooltip
                      title={`Sendung: ${zahl(s.cta_clicks_live)}, Werbevideo: ${zahl(s.cta_clicks_auto_roll)}`}
                    >
                      <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: 11 }}>
                        ({zahl(s.cta_clicks_live)}/{zahl(s.cta_clicks_auto_roll)})
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">{formatPercent(s.click_rate)}</TableCell>
                <TableCell>
                  {s.auto_roll ? (
                    <Tooltip
                      title={`${s.auto_roll.title || 'Video'} · ${formatDuration(s.auto_roll.duration_seconds)} / ${formatDuration(s.auto_roll.video_duration_seconds)}`}
                    >
                      <span>
                        {s.auto_roll.completion_pct != null
                          ? `${zahl(s.auto_roll.completion_pct)} %`
                          : '–'}
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ color: '#999' }}>–</span>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setSnapshotSessionId(s.id)}>
                    anzeigen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {data && data.sessions.length > 0 && (
              <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid #ccc' } }}>
                <TableCell>Summe</TableCell>
                <TableCell>{zahl(data.summary.total_hours, 1)} Std.</TableCell>
                <TableCell align="right">Ø {zahl(data.summary.avg_peak_viewer, 1)}</TableCell>
                <TableCell align="right">{zahl(data.summary.total_cta_clicks)}</TableCell>
                <TableCell align="right">{formatPercent(data.summary.avg_click_rate)}</TableCell>
                <TableCell>—</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={snapshotSessionId != null}
        onClose={() => setSnapshotSessionId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Zuschauerverlauf · Sendung Nr. {snapshotSessionId}</DialogTitle>
        <DialogContent>
          {snapshotLoading && <CircularProgress size={20} />}
          {!snapshotLoading && snapshotData && snapshotData.snapshots.length === 0 && (
            <Alert severity="info">
              Keine Messpunkte für diese Sendung — entweder zu kurz (ein Messpunkt alle 30 s) oder
              vor Einführung dieser Messung. Wird ab der nächsten Sendung gefüllt.
            </Alert>
          )}
          {!snapshotLoading && snapshotData && snapshotData.snapshots.length > 0 && (
            <Box sx={{ height: 360 }}>
              <ResponsiveLine
                data={dropOffLine}
                margin={{ top: 24, right: 110, bottom: 50, left: 50 }}
                xScale={{ type: 'linear', min: 0, max: 'auto' }}
                yScale={{ type: 'linear', min: 0, max: 'auto' }}
                axisBottom={{
                  legend: 'Minuten seit Sendungsstart',
                  legendPosition: 'middle',
                  legendOffset: 36,
                  format: (v) => {
                    const s = Number(v);
                    const m = Math.floor(s / 60);
                    return `${m}:${String(s % 60).padStart(2, '0')}`;
                  },
                }}
                axisLeft={{
                  legend: 'Zuschauer gleichzeitig',
                  legendPosition: 'middle',
                  legendOffset: -40,
                }}
                colors={{ datum: 'color' }}
                pointSize={4}
                useMesh
                lineWidth={2}
                animate={false}
                legends={[
                  {
                    anchor: 'right',
                    direction: 'column',
                    translateX: 100,
                    itemWidth: 90,
                    itemHeight: 18,
                    itemsSpacing: 4,
                    symbolSize: 10,
                  },
                ]}
                markers={
                  snapshotData.phase_switch_sec != null
                    ? [
                        {
                          axis: 'x',
                          value: snapshotData.phase_switch_sec,
                          lineStyle: { stroke: '#ff9800', strokeWidth: 2, strokeDasharray: '4 4' },
                          legend: 'Start Werbevideo',
                          legendPosition: 'top-right',
                        },
                      ]
                    : []
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotSessionId(null)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ minWidth: 280 }}
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default Stats;
